// Copyright (c) Docugami, Inc. All rights reserved.

import { primaryTheme } from "components/shell/presentation/AppStyles";
import { ChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { InteractableComponent, InteractionType } from "sdv/components/InteractableComponent";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { SvgComponent } from "sdv/components/SvgComponent";
import { TableChunkRenderComponent } from "sdv/components/TableChunkRenderComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { Point } from "sdv/Types";
import { boxesEqual, makeBoxFromCenter } from "store/shared_types";
import { getDistanceBetweenPoints } from "utils/MathUtils";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { TokenRange } from "networking/api";
import { copyMultiSelectEntity, createMultiSelectChunkEntity } from "sdv/engine/EntityFactory";

interface PointWithPage extends Point {
    page: number;
}

/**
 * Manages the lifetime of multi selection on the canvas. Injects external selected spans,
 * and handles new control selections, or newly created token ranges.
 */
export class MultiSelectSystem implements ISystem {
    get updateEnabled() { return true; }

    private _selectedMultiSelectName: string = '';

    queries: Queries = {
        multiSelect: { types: [ComponentId.MultiSelect] },
        selectedChunk: { types: [ComponentId.Selected] }
    }

    input!: InputState;
    entities!: IEntityManager;
    resources!: IResources;
    callbacks!: IExternalCallbacks;

    update(): void {
        this.handleStateReset();
        if (!this.resources.get("multiSelect").value) {
            return;
        }

        this.addExternalSelections();
        this.handleMultiSelectRemoveButtonRender();
        this.handleSelectionChanges();
    }

    private handleStateReset() {
        const multiSelect = this.resources.get("multiSelect");
        if (multiSelect.updated && !multiSelect.value) {
            this.queries.multiSelect.query!.entities.forEach(e => e.destroy());
            this._selectedMultiSelectName = '';
        }
    }

    private addExternalSelections() {
        const externalSelections = this.resources.get("externalSelections");
        if (!externalSelections.updated) {
            return;
        }

        this.queries.multiSelect.query!.entities.forEach(e => e.destroy());
        for (const { spanId, tokenRange } of externalSelections.value) {
            createMultiSelectChunkEntity(this.entities, spanId, tokenRange);
        }
    }

    // Ensures the remove button components for each multi-select chunk
    private handleMultiSelectRemoveButtonRender() {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);

        // Render the remove button using the entity's render boxes positioning
        for (const entity of this.queries.multiSelect.query!.entities) {
            const target = this.getTargetPointForChunkRenderComponent(entity);
            if (!target) {
                continue;
            }

            this.addRemoveButtonComponents(entity, target, target.page, cursor);
        }
    }

    private getTargetPointForChunkRenderComponent(entity: Entity): PointWithPage | undefined {
        if (entity.hasComponent(ComponentId.ChunkRender)) {
            const renderBoxes = entity.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!.renderBoxes;
            if (!!renderBoxes.length) {
                // Get the button target center position from the top, right corner of the rendered chunk control
                const firstBox = renderBoxes[0].box;
                return { x: firstBox.left, y: firstBox.top, page: firstBox.page };
            }
        }
        else if (entity.hasComponent(ComponentId.TableChunkRender)) {
            const renderBoxes = entity.getComponent<TableChunkRenderComponent>(ComponentId.TableChunkRender)!.boxes;
            if (!!renderBoxes?.length) {
                // Get the button target center position from the top, right corner of the rendered chunk control
                let targetTop = Infinity;
                let targetLeft = 0;
                let page = 0;
                for (const box of renderBoxes) {
                    if (box.box.top < targetTop) {
                        page = box.box.page;
                        targetTop = box.box.top;
                    }

                    targetLeft = Math.min(targetLeft, box.box.left);
                }

                return { x: targetLeft, y: targetTop, page: page };
            }
        }
    }

    private addRemoveButtonComponents(entity: Entity, target: Point, page: number, cursorPoint: Point | null) {
        const halfBoxLength = 7;
        const isHovered = entity.hasComponent(ComponentId.CursorHit) && !!cursorPoint && getDistanceBetweenPoints(cursorPoint, target) <= halfBoxLength;
        const boxLength = isHovered ? halfBoxLength * 2 : (halfBoxLength - 1) * 2;
        const containingBox = makeBoxFromCenter(target, boxLength, boxLength, page);

        // Ensure the interactable component for the remove click action
        const existing = entity.getComponent<InteractableComponent>(ComponentId.Interactable);
        const shouldForceUpdate = !boxesEqual(containingBox, existing?.hitBox);
        entity.ensureComponent<InteractableComponent>({
            typeId: ComponentId.Interactable,
            interactionType: InteractionType.Click,
            hitBox: containingBox,
            onClick: () => entity.destroy()
        }, shouldForceUpdate);

        // Add the icon component for the remove button
        entity.ensureComponent<SvgComponent>(
            createRemoveChunkButtonSvg({ x: containingBox.left, y: containingBox.top }, isHovered),
            shouldForceUpdate
        );

        entity.ensureComponent(tagComponent(ComponentId.Renderable));
    }

    private handleSelectionChanges() {
        const selectedSpanId = this.resources.get("selectedEntityName");
        if (!selectedSpanId.updated) {
            return;
        }

        const selected = this.queries.selectedChunk.query!.entities[0];
        if (!selected || selected.name !== selectedSpanId.value) {
            return;
        }

        const previousSelected = !!this._selectedMultiSelectName ? this.entities.get(this._selectedMultiSelectName) : null;
        if (previousSelected === selected) {
            return;
        }

        this._selectedMultiSelectName = selected?.name ?? '';

        const getMultiSelectEntity = (spanId: string) =>
            this.queries.multiSelect.query!.entities.find(e => e.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId === spanId);

        // If there is a multi-select entity for the previous selection, ensure that its token range is up-to-date
        if (!!previousSelected) {
            const multiSelect = getMultiSelectEntity(previousSelected.name);
            if (!!multiSelect) {
                // Save any edited token state on this entity
                addOrUpdateTokenRangeComponent(multiSelect, { ...previousSelected.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange });
            }
        }

        // Ensure a multi select component on the selected entity, and remove any existing multi select entity for this span
        if (!!selected) {
            // Clean up any overlapping multi-select spans
            const selectedRange = selected.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            this.queries.multiSelect.query!.entities.forEach(e => {
                const tokenRange = e.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
                const spanId = e.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId;
                if (spanId !== selected.name && TokenRangeUtils.rangesOverlap(tokenRange, selectedRange)) {
                    e.destroy();
                }
            });

            // There isn't an existing multi-select entity for this selection, create a new one
            if (!getMultiSelectEntity(selected.name)) {
                copyMultiSelectEntity(this.entities, selected.name, selectedRange, selected);
            }
        }
    }
}

const createRemoveChunkButtonSvg = (point: Point, isHovered: boolean): SvgComponent => ({
    typeId: ComponentId.Svg,
    point: point,
    paths: [
        {
            path: "M12 2a10 10 0 1 1 0 20a10 10 0 1 1 0-20z",
            color: 'white'
        },
        {
            path: "M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828z M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8z",
            color: isHovered ? primaryTheme.palette.green : primaryTheme.palette.black
        }
    ],
    scale: isHovered ? .7 : .6
})

function addOrUpdateTokenRangeComponent(entity: Entity, targetRange: TokenRange) {
    const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)?.tokenRange;
    if (!tokenRange || !TokenRangeUtils.rangesEqual(tokenRange, targetRange)) {
        // We are explicitly removing and adding the token range component to 
        // force the entity query to be aware of the changes
        entity.removeComponent(ComponentId.TokenRange);
        entity.addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: targetRange
        });
    }
}